import React, {useCallback, useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {Button, message, Modal, Table, Tabs} from "antd";
import type { TableColumnsType } from 'antd';
import ShipFromHereTaskFilter, {DEFAULT_FILTER_OPTIONS, FilterOptions} from "./components/ShipFromHereTaskFilter";
import {TmsRouteTypeEnum, TmsBusinessTypeEnum, TmsTaskStatusEnum, TmsTaskTypeEnum, TmsTaskWaypointStatusEnum, HandoverTypeEnum, TmsVehicleStatus} from "../../../enums/TmsEnum";
import {useMutation, useQuery} from "@apollo/client";
import {GET_TMS_CARRIERS_BY_WAREHOUSE_ID, GetTmsCarriersByWarehouseIdResponse} from "../schedule/graphql/queries/getTmsCarriers";
import {GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, GetHandoverCarriersByWarehouseIdResponse} from "../route/graphql/queries/getHandoverCarriers";
import {GET_TMS_DRIVERS_BY_WAREHOUSE_ID, GetTmsDriversByWarehouseIdResponse} from "../account/graphql/queries/getAllTmsDrivers";
import {GET_HANDOVER_DRIVER_NAMES, GET_HANDOVER_LICENSE_PLATES, GetHandoverDriverNamesResponse, GetHandoverLicensePlatesResponse} from "./graphql/queries/getHandoverFilterOptions";
import {
    GET_TMS_VEHICLES_BY_WAREHOUSE_ID_FOR_SELECT,
    GetTmsVehiclesByWarehouseIdForSelectResponse
} from "../schedule/graphql/queries/getTmsVehiclesWithModel";
import {
    GET_SHIP_FROM_HERE_TASK_LIST,
    ShipFromHereTaskType,
    TmsTaskFiltersInput,
    TmsTaskListingResponse
} from "./graphql/queries/getTmsTasks";
import CreateTemporaryTaskDrawer from "./components/CreateTemporaryTaskDrawer";
import EditTaskDrawer from "./components/EditTaskDrawer";
import CancelTaskModal from "./components/CancelTaskModal";
import {
    BATCH_PUBLISH_TMS_TASKS,
    BatchPublishTmsTasksResponse,
    CANCEL_TMS_TASK,
    CancelTmsTaskResponse,
    DELETE_TMS_TASK,
    UPDATE_TMS_TASK,
    UpdateTmsTaskResponse
} from "./graphql/mutations/taskMutations";
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {hasResource} from '../../../utils/resourceAuth';
import {ResourceCode} from '../../../constants/resourceCodes';
import {GENERATE_TMS_TASK_DOWNLOAD_REPORT, GenerateTmsTaskDownloadReportResponse} from "../download/graphql/mutations/generateTmsTaskDownloadReport";
import { useNavigate } from 'react-router-dom';
import {t} from '../../../i18n';
import {showError} from '../../../utils/exceptionUtils';
import {parseResultArgs, parseResultCode} from '../../../utils/resultCode';
import BolPreviewModalV3, { BolDataV3 } from "./components/bol/v3/BolPreviewModal";
import { TaskGroupType } from "./components/bol/v3/types/bolTypes";
import {useTaskCommon} from './hooks/useTaskCommon';
import {useTaskRouteRowSpan} from './hooks/useTaskRouteRowSpan';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {formatDateTimeRangeToISO} from '../../../utils/dateTimeUtils';
import {getCurrentTimeInTimezone} from '../../../utils/timezoneUtils';


// 任务日期默认区间：结束为当天，向前共 3 天（含当天）
// 任务日期是站点当地日期，优先按 Hub 时区（timezoneLocation，IANA）取「今天」，取不到时退回浏览器本地时间
const getDefaultTaskDate = (timezoneLocation?: string): [string, string] => {
    const now = timezoneLocation ? getCurrentTimeInTimezone(timezoneLocation) : dayjs();
    return [now.subtract(2, 'days').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
};

// 任务是否可编辑
// 1.草稿
// 2.TikTok（须同时满足：未完成、未取消、当前途经点未到达、未待发车）
// 3.本地尾程商交接任务（GA/SCF 等，不限业务来源，与后端 updateTmsTask 放行条件一致；须同时满足：未完成、未取消、当前途经点未到达、未待发车）
const computeTaskCanEdit = (record: any): boolean => {
    const isDraft = record.taskStatus === TmsTaskStatusEnum.Draft;
    const isTikTok = record.businessType === TmsBusinessTypeEnum.TikTok;
    // carrierSourceType 仅交接任务有值，干线/支线任务不受影响
    const isLocalLastMileHandover = record.carrierSourceType === HandoverTypeEnum.LocalLastMileCarrier;
    const isWaypointArrived = record.currentWaypointStatus === TmsTaskWaypointStatusEnum.Arrived;
    const isWaypointPendingDeparture = record.currentWaypointStatus === TmsTaskWaypointStatusEnum.PENDING_DEPARTURE;
    return isDraft
        || ((isTikTok || isLocalLastMileHandover) && record.taskStatus !== TmsTaskStatusEnum.Completed && record.taskStatus !== TmsTaskStatusEnum.Cancelled && !isWaypointArrived && !isWaypointPendingDeparture);
};


/** 取消任务中「任务状态已变化」的 Result.code：提示后关闭弹窗并刷新列表 */
const CANCEL_RESULT_CODE = {
    CANNOT_CANCEL: 'TMS_TASK_CANNOT_CANCEL',
    ORIGIN_DEPARTED: 'TMS_TASK_ORIGIN_DEPARTED_CANNOT_CANCEL',
} as const;
const ShipFromHereTask: React.FC = () => {
    const navigate = useNavigate();
    const { currentWarehouse } = useWarehouseContext();

    // 计算初始 filters，任务日期默认最近 3 天（结束为当天）
    const initialFilters = useMemo(() => {
        return {
            ...DEFAULT_FILTER_OPTIONS,
            routeType: String(TmsRouteTypeEnum.Linehaul),
            taskDate: getDefaultTaskDate(currentWarehouse?.timezoneLocation)
        };
    }, [currentWarehouse?.timezoneLocation]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    // 初始化时默认选中干线（routeType = 1）和默认任务日期
    const [filters, setFilters] = useState<FilterOptions>(initialFilters);
    const [tempFilters, setTempFilters] = useState<FilterOptions>(initialFilters);
    const [filterResetSignal, setFilterResetSignal] = useState(0);
    const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
    const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [drawerMode, setDrawerMode] = useState<'edit' | 'view'>('view');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [bolModalOpen, setBolModalOpen] = useState(false);
    const [bolData, setBolData] = useState<BolDataV3 | null>(null);
    const [selectedTaskForBOL, setSelectedTaskForBOL] = useState<any>(null);
    // 待取消的任务（打开取消弹窗时赋值，关闭置空）
    const [cancelTarget, setCancelTarget] = useState<{ id: string; taskNumber: string } | null>(null);
    const { data: carriersData } = useQuery<GetTmsCarriersByWarehouseIdResponse>(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null
        },
        skip: !currentWarehouse?.id
    });
    const { data: driversData } = useQuery<GetTmsDriversByWarehouseIdResponse>(GET_TMS_DRIVERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null,
            routeType: filters.routeType ? Number(filters.routeType) : null
        },
        skip: !currentWarehouse?.id
    });
    const { data: vehiclesData } = useQuery<GetTmsVehiclesByWarehouseIdForSelectResponse>(GET_TMS_VEHICLES_BY_WAREHOUSE_ID_FOR_SELECT, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null,
            status: TmsVehicleStatus.AVAILABLE,
            routeType: filters.routeType ? Number(filters.routeType) : null
        },
        skip: !currentWarehouse?.id
    });
    const [batchPublishTasks, { loading: publishLoading }] = useMutation<BatchPublishTmsTasksResponse>(BATCH_PUBLISH_TMS_TASKS);
    const [deleteTask, { loading: deleteLoading }] = useMutation(DELETE_TMS_TASK);
    const [, { loading: updateLoading }] = useMutation<UpdateTmsTaskResponse>(UPDATE_TMS_TASK);
    const [cancelTmsTask, { loading: cancelLoading }] = useMutation<CancelTmsTaskResponse>(CANCEL_TMS_TASK);
    const [generateTmsTaskDownloadReport, { loading: downloadReportLoading }] = useMutation<GenerateTmsTaskDownloadReportResponse>(GENERATE_TMS_TASK_DOWNLOAD_REPORT);

    const taskOptions = useMemo(() => [
        { label: String(t('tms.task.taskTypes.regular')), value: TmsTaskTypeEnum.Regular },
        { label: String(t('tms.task.taskTypes.temporary')), value: TmsTaskTypeEnum.Temporary },
        { label: String(t('tms.task.taskTypes.thirdParty')), value: TmsTaskTypeEnum.ThirdParty },
    ], [t]);

    const routeTypeOptions = useMemo(() => [
        { label: String(t('tms.route.routeTypes.linehaul')), value: TmsRouteTypeEnum.Linehaul },
        { label: String(t('tms.route.routeTypes.feeder')), value: TmsRouteTypeEnum.Feeder }
    ], [t]);

    // 根据当前 filters.routeType 计算 Tab 激活项
    const activeRouteTypeTab = filters.routeType ?? String(TmsRouteTypeEnum.Linehaul);
    const isHandoverTab = activeRouteTypeTab === String(TmsRouteTypeEnum.Handover);

    // 交接类型：从 tms_task 表查去重的司机名称和车牌号
    const { data: handoverDriverData } = useQuery<GetHandoverDriverNamesResponse>(GET_HANDOVER_DRIVER_NAMES, {
        variables: { warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0 },
        skip: !isHandoverTab || !currentWarehouse?.id
    });
    const { data: handoverVehicleData } = useQuery<GetHandoverLicensePlatesResponse>(GET_HANDOVER_LICENSE_PLATES, {
        variables: { warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0 },
        skip: !isHandoverTab || !currentWarehouse?.id
    });

    const { data: handoverCarriersData } = useQuery<GetHandoverCarriersByWarehouseIdResponse>(GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0
        },
        skip: !isHandoverTab || !currentWarehouse?.id
    });
    const handoverCarrierOptions = useMemo(() => {
        const list = handoverCarriersData?.getHandoverCarriersByWarehouseId ?? [];
        return list.map(hc => ({ label: hc.carrierName, value: hc.id }));
    }, [handoverCarriersData]);

    const handleRouteTypeTabChange = (key: string) => {
        // 切换 tab 时清空路线选项、班次选项、承运商选项和发车时间，然后根据条件进行查询
        const nextFilters: FilterOptions = {
            ...filters,
            routeType: key,
            routeId: '', // 清空路线选项
            scheduleId: '', // 清空班次选项
            carrierId: '', // 清空承运商选项
            departureTime: null // 清空发车时间（仅交接 tab 展示，切走后不应残留）
        };
        setTempFilters(nextFilters);
        setFilters(nextFilters);
        setCurrentPage(1);
    };

    const businessTypeOptions = useMemo(() => [
        { label: String(t('tms.task.businessTypes.ownRoute')), value: TmsBusinessTypeEnum.OwnRoute },
        { label: String(t('tms.task.businessTypes.temuGA')), value: TmsBusinessTypeEnum.TemuGA },
        { label: String(t('tms.task.businessTypes.temuSCF')), value: TmsBusinessTypeEnum.TemuSCF },
        { label: String(t('tms.task.businessTypes.tiktok')), value: TmsBusinessTypeEnum.TikTok },
    ], [t]);

    const taskStatusOptions = useMemo(() => [
        { label: String(t('tms.task.status.draft')), value: TmsTaskStatusEnum.Draft },
        { label: String(t('tms.task.status.published')), value: TmsTaskStatusEnum.Published },
        { label: String(t('tms.task.status.inProgress')), value: TmsTaskStatusEnum.InProgress },
        { label: String(t('tms.task.status.completed')), value: TmsTaskStatusEnum.Completed },
        { label: String(t('tms.task.status.abnormal')), value: TmsTaskStatusEnum.Abnormal },
        { label: String(t('tms.task.status.cancelled')), value: TmsTaskStatusEnum.Cancelled }
    ], [t]);

    const UNFILLED = '__unfilled__';
    const unfilledLabel = String(t('tms.task.filters.unfilled'));

    const driverOptions = useMemo(() => {
        if (isHandoverTab) {
            const options = (handoverDriverData?.getHandoverDriverNames || []).map(name => ({ label: name, value: name }));
            return [{ label: unfilledLabel, value: UNFILLED }, ...options];
        }
        if (!driversData?.getTmsDriversByWarehouseId) return [];
        return driversData.getTmsDriversByWarehouseId.map(driver => ({
            label: driver.driverName,
            value: driver.id
        }));
    }, [driversData, handoverDriverData, isHandoverTab, unfilledLabel]);

    const vehicleOptions = useMemo(() => {
        if (isHandoverTab) {
            const options = (handoverVehicleData?.getHandoverLicensePlates || []).map(plate => ({ label: plate, value: plate }));
            return [{ label: unfilledLabel, value: UNFILLED }, ...options];
        }
        if (!vehiclesData?.getTmsVehiclesByWarehouseIdForSelect) return [];
        return vehiclesData.getTmsVehiclesByWarehouseIdForSelect.map(vehicle => ({
            label: vehicle.licensePlate,
            value: vehicle.id.toString()
        }));
    }, [vehiclesData, handoverVehicleData, isHandoverTab, unfilledLabel]);

    // 当 Hub 改变时，重置分页到第一页，并按新 Hub 时区重置默认任务日期
    useEffect(() => {
        if (currentWarehouse?.id) {
            setCurrentPage(1);
            const defaultTaskDate = getDefaultTaskDate(currentWarehouse?.timezoneLocation);
            setFilters(prev => ({ ...prev, taskDate: defaultTaskDate }));
            setTempFilters(prev => ({ ...prev, taskDate: defaultTaskDate }));
        }
    }, [currentWarehouse?.id, currentWarehouse?.timezoneLocation]);

    // 构建查询过滤条件（已点击「筛选」后生效，用于列表查询）
    const queryFilters: TmsTaskFiltersInput = useMemo(() => {
        // 发车时间（实际发车）仅交接 tab 生效
        // 选择器给的是不带时区的本地时间串，按浏览器时区换算成 UTC ISO，与库里 actual_departure_at（UTC）对齐
        const departureTimeRange = isHandoverTab ? formatDateTimeRangeToISO(filters.departureTime) : undefined;
        return {
            taskType: filters.taskType ? Number(filters.taskType) : undefined,
            businessType: filters.businessType ? Number(filters.businessType) : undefined,
            routeId: filters.routeId ? Number(filters.routeId) : undefined,
            routeCode: filters.routeCode || undefined,
            taskNumber: filters.taskNumber || undefined,
            taskStatuses: filters.taskStatus.length > 0 ? filters.taskStatus.map(s => Number(s)) : undefined,
            routeType: filters.routeType ? Number(filters.routeType) : undefined,
            scheduleId: filters.scheduleId ? Number(filters.scheduleId) : undefined,
            carrierId: filters.carrierId ? Number(filters.carrierId) : undefined,
            driverId: isHandoverTab ? undefined : (filters.driverId ? Number(filters.driverId) : undefined),
            vehicleId: isHandoverTab ? undefined : (filters.vehicleId ? Number(filters.vehicleId) : undefined),
            driverName: isHandoverTab ? (filters.driverId === UNFILLED ? '' : filters.driverId || undefined) : undefined,
            licensePlate: isHandoverTab ? (filters.vehicleId === UNFILLED ? '' : filters.vehicleId || undefined) : undefined,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
            taskDateStart: filters.taskDate?.[0] || undefined,
            taskDateEnd: filters.taskDate?.[1] || undefined,
            departureTimeStart: departureTimeRange?.start,
            departureTimeEnd: departureTimeRange?.end,
        };
    }, [filters, currentWarehouse?.id]);

    // 下载时使用当前表单的筛选条件（与是否点击「筛选」无关），保证下载数据与用户勾选条件一致
    const downloadFilters: TmsTaskFiltersInput = useMemo(() => {
        const departureTimeRange = isHandoverTab ? formatDateTimeRangeToISO(tempFilters.departureTime) : undefined;
        return {
            taskType: tempFilters.taskType ? Number(tempFilters.taskType) : undefined,
            businessType: tempFilters.businessType ? Number(tempFilters.businessType) : undefined,
            routeId: tempFilters.routeId ? Number(tempFilters.routeId) : undefined,
            routeCode: tempFilters.routeCode || undefined,
            taskNumber: tempFilters.taskNumber || undefined,
            taskStatuses: tempFilters.taskStatus?.length ? tempFilters.taskStatus.map(s => Number(s)) : undefined,
            routeType: tempFilters.routeType ? Number(tempFilters.routeType) : undefined,
            scheduleId: tempFilters.scheduleId ? Number(tempFilters.scheduleId) : undefined,
            carrierId: tempFilters.carrierId ? Number(tempFilters.carrierId) : undefined,
            // 交接 tab 下司机/车辆下拉的 value 是司机名和车牌字符串，须走 driverName/licensePlate，
            // 否则 Number() 得到 NaN 导致条件丢失、下载到全量数据（与 queryFilters 保持一致）
            driverId: isHandoverTab ? undefined : (tempFilters.driverId ? Number(tempFilters.driverId) : undefined),
            vehicleId: isHandoverTab ? undefined : (tempFilters.vehicleId ? Number(tempFilters.vehicleId) : undefined),
            driverName: isHandoverTab ? (tempFilters.driverId === UNFILLED ? '' : tempFilters.driverId || undefined) : undefined,
            licensePlate: isHandoverTab ? (tempFilters.vehicleId === UNFILLED ? '' : tempFilters.vehicleId || undefined) : undefined,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
            taskDateStart: tempFilters.taskDate?.[0] ?? undefined,
            taskDateEnd: tempFilters.taskDate?.[1] ?? undefined,
            departureTimeStart: departureTimeRange?.start,
            departureTimeEnd: departureTimeRange?.end,
        };
    }, [tempFilters, currentWarehouse?.id, isHandoverTab]);

    // Query for task listing
    const { data, loading, refetch } = useQuery<TmsTaskListingResponse>(GET_SHIP_FROM_HERE_TASK_LIST, {
        variables: {
            filter: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
            }
        },
        skip: !currentWarehouse?.id,
        fetchPolicy: 'no-cache'
    });

    const taskData = data?.getShipFromHereTaskList?.content || [];
    const totalElements = data?.getShipFromHereTaskList?.totalElements || 0;

    // 使用任务通用逻辑 hook
    const { generateBolData, getBolInfos, getTaskStatusClassName, getWaypointStatusClassName, getWaypointStatusText } = useTaskCommon();
    const { routeRowSpanMap, taskDateRowSpanMap } = useTaskRouteRowSpan(taskData);


    /**
     * 处理批量发布任务的错误
     * @param error 错误对象
     * @param refetch 刷新数据的函数
     */
    const handleBatchPublishError = async (error: any, refetch: () => Promise<any>) => {
        console.error('Failed to publish tasks:', error);

        const details = error?.graphQLErrors?.[0]?.extensions?.details;
        if (!details) {
            message.error(error.message || t('tms.task.messages.failedToPublishTasks'));
            return;
        }

        const { successCount = 0, failCount = 0, errors = [] } = details;
        const summaryMessage = t('tms.task.messages.batchPublishSummary', { successCount, failCount });

        const errorDetails = errors.map((err: any) => {
            const match = err.taskInfos?.[0]?.details?.errorMessage?.match(/errors\.[a-zA-Z0-9_.]+/);
            const translatedKey = match ? t(match[0]) : null;

            const taskDetails = err.taskInfos?.[0]?.details ?
                { ...err.taskInfos[0].details, errorMessage: translatedKey || err.taskInfos[0].details.errorMessage }
                : {};

            let msg = t(err.messageKey, taskDetails);

            if (err.taskNumbers?.length > 0) {
                const nums = err.taskNumbers.slice(0, 5).join(', ') +
                    (err.taskNumbers.length > 5 ? ` (+${err.taskNumbers.length - 5})` : '');
                msg = `${msg} (${t('tms.task.messages.taskNumbers')}: ${nums})`;
            }

            return msg;
        });

        const errorMessage = `${summaryMessage}\n${errorDetails.join('\n')}`;
        await refetch().catch(() => {});

        message.error({
            content: <div style={{ whiteSpace: 'pre-wrap', maxWidth: '600px', textAlign: 'left' }}>{errorMessage}</div>,
            duration: 3,
        });
        setSelectedRowKeys([]);
    };

    const handlePublish = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('tms.task.messages.pleaseSelectAtLeastOneTask'));
            return;
        }

        // Filter tasks with Draft status
        const draftTasks = taskData.filter((task: any) => 
            selectedRowKeys.includes(task.id) && task.taskStatus === TmsTaskStatusEnum.Draft
        );

        if (draftTasks.length === 0) {
            message.warning(t('tms.task.messages.noDraftTasksFound'));
            return;
        }

        try {
            const taskIds = draftTasks.map((task: any) => task.id);
            await batchPublishTasks({
                variables: { taskIds },
                refetchQueries: [{ query: GET_SHIP_FROM_HERE_TASK_LIST, variables: { filter: queryFilters, pageRequest: { page: currentPage, size: pageSize } } }]
            });
            const successMessage = draftTasks.length === 1 
                ? t('tms.task.messages.successfullyPublished', { count: draftTasks.length })
                : t('tms.task.messages.successfullyPublishedPlural', { count: draftTasks.length });
            message.success(successMessage);
            setSelectedRowKeys([]);
            // 显式刷新页面数据
            await refetch();
        } catch (error: any) {
            await handleBatchPublishError(error, refetch);
        }
    };

    const handleDelete = async (taskId: string) => {
        Modal.confirm({
            title: String(t('tms.task.messages.deleteDraftTask')),
            content: String(t('tms.task.messages.confirmDeleteDraftTask')),
            okText: String(t('tms.task.buttons.delete')),
            okType: 'danger',
            cancelText: String(t('tms.task.buttons.cancel')),
            onOk: async () => {
                try {
                    await deleteTask({
                        variables: { id: taskId },
                        refetchQueries: [{ query: GET_SHIP_FROM_HERE_TASK_LIST, variables: { filter: queryFilters, pageRequest: { page: currentPage, size: pageSize } } }]
                    });
                    message.success(t('tms.task.messages.taskDeletedSuccessfully'));
                    await refetch();
                } catch (error: any) {
                    console.error('Failed to delete task:', error);
                    const gqlExtensions = error?.graphQLErrors?.[0]?.extensions;
                    const hasMessageKey = gqlExtensions?.messageKey;
                    // 后端 details.currentStatus 是状态码，翻译成状态名再插值，避免给用户展示原始数字
                    if (hasMessageKey === 'errors.business.tms.task.cannotDeleteNonDraft') {
                        const currentStatus = Number(gqlExtensions?.details?.currentStatus);
                        message.error(String(t(hasMessageKey, { currentStatus: getTaskStatusText(currentStatus) })));
                        // 列表展示的是旧状态，刷新后按钮按最新状态展示
                        await refetch();
                    } else {
                        hasMessageKey ? showError(error) : message.error(error?.message || t('tms.task.messages.failedToDeleteTask'));
                    }
                }
            }
        });
    };

    // 取消弹窗确认：提交取消原因与取消成本（美分）；业务失败以 Result.code 返回
    const handleCancelConfirm = async (cancelReason: string, cancelCostCents: number) => {
        if (!cancelTarget) return;
        try {
            const { data } = await cancelTmsTask({
                variables: { id: cancelTarget.id, cancelReason, cancelCost: cancelCostCents },
                refetchQueries: [{ query: GET_SHIP_FROM_HERE_TASK_LIST, variables: { filter: queryFilters, pageRequest: { page: currentPage, size: pageSize } } }]
            });
            const res = data?.cancelTmsTask;
            if (res?.success) {
                message.success(t('tms.task.messages.taskCancelledSuccessfully'));
                setCancelTarget(null);
                await refetch();
                return;
            }
            const args = parseResultArgs(res?.argsJson);
            if (res?.code === CANCEL_RESULT_CODE.CANNOT_CANCEL || res?.code === CANCEL_RESULT_CODE.ORIGIN_DEPARTED) {
                // 任务状态已变化：后端 currentStatus 是状态码，翻译成状态名再插值；关闭弹窗并刷新，按钮按最新状态展示
                const currentStatus = Number(args?.currentStatus);
                message.error(parseResultCode(res.code, res.message, { ...args, currentStatus: getTaskStatusText(currentStatus) }));
                setCancelTarget(null);
                await refetch();
                return;
            }
            // 参数类错误等：保留弹窗，便于修改后重试
            message.error(parseResultCode(res?.code ?? '', res?.message ?? undefined, args) || t('tms.task.messages.failedToCancelTask'));
        } catch (error: any) {
            console.error('Failed to cancel task:', error);
            showError(error);
        }
    };

    // 根据任务状态值，返回文本信息
    const getTaskStatusText = (status: number) => {
        return taskStatusOptions.find(option => option.value === status)?.label || String(t('tms.task.status.unknown'))
    }

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys: React.Key[]) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record: any) => ({
            disabled: record.taskStatus !== TmsTaskStatusEnum.Draft,
        }),
    };

    const columns: TableColumnsType<any> = useMemo(() => [
        {
            title: String(t('tms.task.columns.route')),
            dataIndex: 'routeName',
            key: 'routeName',
            align: 'center',
            width: 160,
            fixed: 'left',
            render: (routeName: string | null | undefined, record: any) => {
                if (!routeName || !record.routeId) return '-';
                return (
                    <span className="whitespace-normal" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {routeName}
                        {record.routeCode && <span>（{record.routeCode}）</span>}
                    </span>
                );
            },
            onCell: (_: any, index?: number) => {
                if (index === undefined) return {};
                const rowSpanInfo = routeRowSpanMap.get(index);
                if (rowSpanInfo) {
                    return {
                        rowSpan: rowSpanInfo.rowSpan,
                    };
                }
                return { rowSpan: 1 };
            },
        },
        {
            title: String(t('tms.task.columns.businessType')),
            dataIndex: 'businessType',
            key: 'businessType',
            align: 'center',
            width: 140,
            fixed: 'left',
            render: (value: number) => {
                const option = businessTypeOptions.find(option => option.value === value);
                return option?.label || '-';
            }
        },
        {
            title: String(t('tms.task.columns.taskNumber')),
            dataIndex: 'taskNumber',
            key: 'taskNumber',
            align: 'center',
            width: 160,
            fixed: 'left',
            render: (taskNumber: string, record: any) => {
                const canEdit = computeTaskCanEdit(record);
                return (
                    <Button
                        type="link"
                        onClick={() => {
                            setSelectedTaskId(record.id);
                            setDrawerMode(canEdit ? 'edit' : 'view');
                            setTaskDrawerOpen(true);
                        }}
                        className="p-0 h-auto whitespace-normal"
                        style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                        {taskNumber}
                    </Button>
                );
            },
        },
        {
            title: String(t('tms.task.columns.taskDate')),
            dataIndex: 'taskDate',
            key: 'taskDate',
            align: 'center',
            width: 120,
            fixed: 'left',
            render: (date: string) => date || '-',
            onCell: (_: any, index?: number) => {
                if (index === undefined) return {};
                const rowSpanInfo = taskDateRowSpanMap.get(index);
                if (rowSpanInfo) {
                    return {
                        rowSpan: rowSpanInfo.rowSpan,
                    };
                }
                return { rowSpan: 1 };
            },
        },
        {
            title: String(t('tms.task.columns.taskType')),
            dataIndex: 'taskType',
            key: 'taskType',
            align: 'center',
            width: 120,
            render: (type: number) => {
                const typeOption = taskOptions.find(option => option.value === type);
                return typeOption?.label || '-';
            }
        },
        {
            title: String(t('tms.task.columns.planDepartureTime')),
            dataIndex: 'planDepartureAt',
            key: 'planDepartureTime',
            align: 'center',
            width: 180,
            render: (_: unknown, record: any) => {
                const { planDepartureDate, planDepartureTime, planDepartureTimezone } = record;
                const parts = [planDepartureDate, planDepartureTime, planDepartureTimezone].filter(Boolean);
                return parts.length > 0 ? parts.join(' ') : '-';
            },
        },
        {
            title: String(t('tms.task.columns.vehicleEstimatedArrivalTime')),
            dataIndex: 'vehicleEstimatedArrivalAt',
            key: 'vehicleEstimatedArrivalTime',
            align: 'center',
            width: 180,
            render: (_: unknown, record: any) => {
                const { vehicleEstimatedArrivalDate, vehicleEstimatedArrivalTime, vehicleEstimatedArrivalTimezone } = record;
                const parts = [vehicleEstimatedArrivalDate, vehicleEstimatedArrivalTime, vehicleEstimatedArrivalTimezone].filter(Boolean);
                return parts.length > 0 ? parts.join(' ') : '-';
            },
        },
        {
            title: String(t('tms.task.columns.currentStation')),
            dataIndex: 'currentStation',
            key: 'currentStation',
            align: 'center',
            width: 120,
            render: (currentStation: string | null | undefined) => currentStation || '-',
        },
        {
            title: String(t('tms.task.columns.taskStatus')),
            dataIndex: 'taskStatus',
            key: 'taskStatus',
            align: 'center',
            width: 120,
            render: (status: number, record: any) => {
                let statusText: string;
                let className: string;
                switch(status) {
                    case TmsTaskStatusEnum.Draft:
                    case TmsTaskStatusEnum.Completed:
                    case TmsTaskStatusEnum.Cancelled:
                    case TmsTaskStatusEnum.Abnormal:
                        className = getTaskStatusClassName(status);
                        statusText = getTaskStatusText(status);
                        break;

                    case TmsTaskStatusEnum.Published:
                    case TmsTaskStatusEnum.InProgress:
                        // 获取途经点状态
                        const waypointStatus = record.currentWaypointStatus;
                        className = getWaypointStatusClassName(waypointStatus);
                        statusText = getWaypointStatusText(waypointStatus);
                        break;

                    default:
                        className = getTaskStatusClassName(status);
                        statusText = getTaskStatusText(status);
                }

                return (
                    <Button
                        size="small"
                        className={`${className} cursor-default hover:opacity-80 border`}
                        style={{ pointerEvents: 'none' }}
                    >
                        {statusText}
                    </Button>
                );
            }
        },
        {
            title: activeRouteTypeTab === String(TmsRouteTypeEnum.Handover)
                ? String(t('tms.route.columns.handoverCarrier'))
                : String(t('tms.task.columns.carrier')),
            dataIndex: 'carrierName',
            key: 'carrierName',
            align: 'center',
            width: 140,
            render: (carrierName: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {carrierName || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.vehicleModel')),
            dataIndex: 'vehicleTypeName',
            key: 'vehicleTypeName',
            width: 150,
            align: 'center',
            render: (vehicleTypeName: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {vehicleTypeName || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.escortCount')),
            dataIndex: 'escortCount',
            key: 'escortCount',
            width: 120,
            align: 'center',
            render: (escortCount: number) => escortCount || '-',
        },
        {
            title: String(t('tms.task.columns.driver')),
            dataIndex: 'driverName',
            key: 'driverName',
            align: 'center',
            width: 120,
            render: (driverName: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {driverName || '-'}
                </span>
            )
        },
        {
            title: String(t('tms.task.columns.gpsTracking')),
            dataIndex: 'gpsTracking',
            key: 'gpsTracking',
            width: 160,
            align: 'center',
            render: (gpsTracking: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {gpsTracking || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.bolNumber')),
            dataIndex: 'bolNumber',
            key: 'bolNumber',
            align: 'center',
            width: 180,
            render: (bolNumber: string) => {
                if (!bolNumber) {
                    return <span>-</span>;
                }
                const parts = bolNumber.split(',').map(part => part.trim()).filter(Boolean);
                return (
                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {parts.map((part, index) => (
                            <div key={index}>{part}</div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: String(t('tms.task.columns.vehicle')),
            dataIndex: 'licensePlate',
            key: 'licensePlate',
            align: 'center',
            width: 120,
            render: (licensePlate: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {licensePlate || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.action')),
            key: 'action',
            width: 80,
            align: 'center',
            fixed: 'right',
            render: (_, record: any) => {
                const taskStatus = record.taskStatus;
                const isDraft = taskStatus === TmsTaskStatusEnum.Draft;
                const isTikTok = record.businessType === TmsBusinessTypeEnum.TikTok;
                const canEdit = computeTaskCanEdit(record);
                // 可取消：已发布，或进行中且始发站未发车（始发发车后才回填任务级实际发车时间）；TikTok 任务不支持取消
                const canCancel = !isTikTok && (
                    taskStatus === TmsTaskStatusEnum.Published ||
                    (taskStatus === TmsTaskStatusEnum.InProgress && !record.actualDepartureAt)
                );

                return (
                    <div className="flex gap-1 justify-center flex-nowrap">
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                setSelectedTaskId(record.id);
                                setDrawerMode(canEdit ? 'edit' : 'view');
                                setTaskDrawerOpen(true);
                            }}
                        >
                            {canEdit ? String(t('tms.task.buttons.edit')) : String(t('tms.task.buttons.view'))}
                        </Button>
                        {isDraft && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    handleDelete(record.id);
                                }}
                                loading={deleteLoading}
                            >
                                {String(t('tms.task.buttons.delete'))}
                            </Button>
                        )}
                        {canCancel && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    setCancelTarget({ id: record.id, taskNumber: record.taskNumber });
                                }}
                            >
                                {String(t('tms.task.buttons.cancel'))}
                            </Button>
                        )}
                         {!isDraft && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    handleBOLPrintWithModal(record);
                                }}
                            >
                                {String(t('tms.task.buttons.printBOL'))}
                            </Button>
                        )}
                    </div>
                );
            }
        }
    ], [taskOptions, taskStatusOptions, routeTypeOptions, activeRouteTypeTab, currentWarehouse, routeRowSpanMap, taskDateRowSpanMap, deleteLoading, updateLoading, t, getTaskStatusClassName, getWaypointStatusText, getWaypointStatusClassName]);

    const handleFilter = () => {
        setFilters({ ...tempFilters });
        setCurrentPage(1);
    };

    const handleDownloadReport = async () => {
        try {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await generateTmsTaskDownloadReport({
                variables: {
                    filters: downloadFilters,
                    taskListType: 'SHIP_FROM_HERE',
                    timezone: userTimezone,
                },
            });
            const reportId = res.data?.generateTmsTaskDownloadReport;
            if (reportId) {
                message.success(t('tms.task.download.submitted') || '报表已提交，请到「下载」页查看并下载');
                if (hasResource([ResourceCode.menu.TMS_DOWNLOAD])) {
                    navigate('/tms/download');
                }
            }
        } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : '';
            message.error(msg || (t('tms.task.download.failed') || '提交下载失败'));
        }
    };

    const handleResetFilters = () => {
        const resetFiltersValue: FilterOptions = {
            ...DEFAULT_FILTER_OPTIONS,
            routeType: filters.routeType || String(TmsRouteTypeEnum.Linehaul),
            taskDate: getDefaultTaskDate(currentWarehouse?.timezoneLocation)
        };
        setFilterResetSignal(s => s + 1);
        setTempFilters(resetFiltersValue);
        setFilters(resetFiltersValue);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const handleCreateSuccess = () => {
        refetch();
    };

    const handleBOLPrintWithModal = useCallback(
        (task: ShipFromHereTaskType) => {
            generateBolData(task, getBolInfos, setBolData, setSelectedTaskForBOL, setBolModalOpen);
        },
        [generateBolData, getBolInfos]
    );

    // @ts-ignore
    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* Filter Area */}
                <ShipFromHereTaskFilter
                    taskOptions={taskOptions}
                    statusOptions={taskStatusOptions}
                    routeTypeOptions={routeTypeOptions}
                    carriersData={carriersData?.getTmsCarriersByWarehouseId}
                    handoverCarrierOptions={handoverCarrierOptions}
                    isHandoverTab={isHandoverTab}
                    driverOptions={driverOptions}
                    vehicleOptions={vehicleOptions}
                    initialFilters={initialFilters}
                    currentRouteType={filters.routeType}
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    resetSignal={filterResetSignal}
                    businessTypeOptions={businessTypeOptions}
                />

                {/* Action Buttons + Tabs */}
                <div className="flex justify-between items-center mb-3 pl-0">
                    <div>
                        <Tabs
                            activeKey={activeRouteTypeTab}
                            onChange={handleRouteTypeTabChange}
                            items={[
                                { key: String(TmsRouteTypeEnum.Linehaul), label: t('tms.route.routeTypes.linehaul') },
                                { key: String(TmsRouteTypeEnum.Feeder), label: t('tms.route.routeTypes.feeder') },
                                { key: String(TmsRouteTypeEnum.Handover), label: t('tms.route.routeTypes.handover') },
                            ]}
                            className="[&_.ant-tabs-tab]:font-bold"
                        />
                    </div>

                    <div className="flex gap-2">
                        <Button
                            onClick={() => setCreateDrawerOpen(true)}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.task.buttons.createTemporaryTask'))}</span>
                        </Button>
                        <Button
                            onClick={handleDownloadReport}
                            loading={downloadReportLoading}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.menu.download'))}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.task.filters.filter'))}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.task.filters.reset'))}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handlePublish}
                            loading={publishLoading}
                            disabled={selectedRowKeys.length === 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.task.buttons.publish'))}</span>
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={taskData}
                        bordered
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        rowSelection={rowSelection}
                        size="middle"
                        tableLayout="auto"
                        className="mb-4"
                    />
                </div>

                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={(page, size) => handlePageChange(page, size)}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* Create Temporary Task Drawer */}
                <CreateTemporaryTaskDrawer
                    open={createDrawerOpen}
                    onClose={() => setCreateDrawerOpen(false)}
                    onSubmit={handleCreateSuccess}
                />

                {/* Task Drawer (Edit/View) */}
                <EditTaskDrawer
                    open={taskDrawerOpen}
                    onClose={() => {
                        setTaskDrawerOpen(false);
                        setSelectedTaskId(null);
                        // drawer 内的到站/发车/装卸车等操作只刷新了任务详情，关闭时统一刷新列表，避免按钮按旧状态展示
                        refetch();
                    }}
                    taskId={selectedTaskId}
                    mode={drawerMode}
                />

                {/* 取消任务弹窗 */}
                <CancelTaskModal
                    open={!!cancelTarget}
                    taskNumber={cancelTarget?.taskNumber}
                    confirmLoading={cancelLoading}
                    onOk={handleCancelConfirm}
                    onCancel={() => setCancelTarget(null)}
                />

                {/* BOL Preview Modal（统一 v3） */}
                <BolPreviewModalV3
                    open={bolModalOpen}
                    onClose={() => {
                        setBolModalOpen(false);
                        setSelectedTaskForBOL(null);
                    }}
                    initialBOLData={bolData}
                    taskGroupType={TaskGroupType.Departure}
                />

            </div>
    );
};

export default ShipFromHereTask;

